// src/pages/admin/UserManagementPage.jsx
// Super Admin user management - standardized header with logo
// Updated: Added disable/enable and delete functionality

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { trackEvent, trackPageView } from "../../utils/analytics";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.invenire.app";

export default function UserManagementPage() {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  
  // State
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [roles] = useState(["super_admin", "admin", "recruiter"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [selectedTenant, setSelectedTenant] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showPending, setShowPending] = useState(true);
  
  // Modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalRole, setModalRole] = useState("recruiter");
  const [modalTenant, setModalTenant] = useState("");
  const [saving, setSaving] = useState(false);

  // Set document title
  useEffect(() => {
    document.title = 'Invenire - User Management';
  }, []);

  // Track page view
  useEffect(() => {
    trackPageView('/admin/users', 'User Management');
  }, []);

  // Load users and tenants
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load tenants first
      const tenantsRes = await authFetch(`${API_BASE}/tenants`);
      if (tenantsRes.ok) {
        const tenantsData = await tenantsRes.json();
        setTenants(tenantsData);
      }
      
      // Load users
      let usersUrl = `${API_BASE}/auth/users`;
      if (selectedTenant) {
        usersUrl += `?tenant_id=${selectedTenant}`;
      }
      
      const usersRes = await authFetch(usersUrl);
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData);
      } else {
        throw new Error("Failed to load users");
      }
      
      // Load pending users
      const pendingRes = await authFetch(`${API_BASE}/auth/users/pending`);
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingUsers(pendingData);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, selectedTenant]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter users by search
  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      u.email?.toLowerCase().includes(query) ||
      u.name?.toLowerCase().includes(query)
    );
  });
  
  // Filter pending users by search
  const filteredPendingUsers = pendingUsers.filter(u => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      u.email?.toLowerCase().includes(query) ||
      u.name?.toLowerCase().includes(query)
    );
  });

  // Approve pending user
  async function handleApproveUser() {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      const params = new URLSearchParams({ role_name: modalRole });
      if (modalTenant) {
        params.append("tenant_id", modalTenant);
      }
      
      const res = await authFetch(
        `${API_BASE}/auth/users/${selectedUser.id}/approve?${params}`,
        { method: "POST" }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to approve user");
      }
      
      trackEvent("user_approved", { user_email: selectedUser.email });
      
      await loadData();
      setShowApproveModal(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Reject pending user
  async function handleRejectUser(pendingUser) {
    if (!window.confirm(`Reject access request from ${pendingUser.email}? This will delete their pending request.`)) {
      return;
    }
    
    try {
      const res = await authFetch(
        `${API_BASE}/auth/users/${pendingUser.id}/reject`,
        { method: "POST" }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to reject user");
      }
      
      trackEvent("user_rejected", { user_email: pendingUser.email });
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  // Assign role to user
  async function handleAssignRole() {
    if (!selectedUser || !modalRole) return;
    
    setSaving(true);
    try {
      const params = new URLSearchParams({ role_name: modalRole });
      if (modalTenant) {
        params.append("tenant_id", modalTenant);
      }
      
      const res = await authFetch(
        `${API_BASE}/auth/users/${selectedUser.id}/roles?${params}`,
        { method: "POST" }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to assign role");
      }
      
      trackEvent("user_role_assigned", {
        role: modalRole,
        has_tenant: !!modalTenant
      });
      
      setShowRoleModal(false);
      loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Remove role from user (kept for future use)
  // eslint-disable-next-line no-unused-vars
  async function handleRemoveRole(userId, roleName, tenantId) {
    if (!window.confirm(`Remove ${roleName} role from this user?`)) return;
    
    try {
      let url = `${API_BASE}/auth/users/${userId}/roles/${roleName}`;
      if (tenantId) {
        url += `?tenant_id=${tenantId}`;
      }
      
      const res = await authFetch(url, { method: "DELETE" });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to remove role");
      }
      
      trackEvent("user_role_removed", { role: roleName });
      loadData();
    } catch (err) {
      alert(err.message);
    }
  }

  // Add user to tenant
  async function handleAddToTenant() {
    if (!selectedUser || !modalTenant) return;
    
    setSaving(true);
    try {
      const res = await authFetch(
        `${API_BASE}/auth/users/${selectedUser.id}/tenants/${modalTenant}`,
        { method: "POST" }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to add user to tenant");
      }
      
      trackEvent("user_added_to_tenant");
      setShowTenantModal(false);
      loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Disable/Enable user
  async function handleToggleUserStatus(userToToggle) {
    const newStatus = userToToggle.is_disabled ? "enable" : "disable";
    const confirmMsg = userToToggle.is_disabled 
      ? `Enable ${userToToggle.email}? They will be able to sign in again.`
      : `Disable ${userToToggle.email}? They will not be able to sign in until re-enabled.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const res = await authFetch(
        `${API_BASE}/auth/users/${userToToggle.id}/${newStatus}`,
        { method: "POST" }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || `Failed to ${newStatus} user`);
      }
      
      trackEvent(`user_${newStatus}d`, { user_email: userToToggle.email });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  // Delete user
  async function handleDeleteUser() {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      const res = await authFetch(
        `${API_BASE}/auth/users/${selectedUser.id}`,
        { method: "DELETE" }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to delete user");
      }
      
      trackEvent("user_deleted", { user_email: selectedUser.email });
      
      await loadData();
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img 
            src="/invenire-logo-transparent.png" 
            alt="Invenire" 
            style={styles.logoImage}
          />
          <span style={styles.subtitle}>User Management</span>
        </div>
        <button onClick={() => navigate("/admin")} style={styles.backButton}>
          ← Admin Home
        </button>
      </div>

      <div style={styles.content}>
        <h2 style={styles.pageTitle}>Manage Users</h2>
        <p style={styles.pageSubtitle}>
          Manage users, roles, and tenant access across the platform.
        </p>

        {/* Filters */}
        <div style={styles.filters}>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          
          <select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            style={styles.select}
          >
            <option value="">All Tenants</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          
          <button onClick={loadData} style={styles.refreshBtn}>
            ↻ Refresh
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={styles.error}>
            {error}
            <button onClick={() => setError(null)} style={styles.errorDismiss}>✕</button>
          </div>
        )}

        {/* Pending Users Section */}
        {filteredPendingUsers.length > 0 && (
          <div style={styles.pendingSection}>
            <div 
              style={styles.pendingHeader}
              onClick={() => setShowPending(!showPending)}
            >
              <h2 style={styles.pendingTitle}>
                ⏳ Pending Approval ({filteredPendingUsers.length})
              </h2>
              <span style={styles.toggleIcon}>{showPending ? '▼' : '▶'}</span>
            </div>
            
            {showPending && (
              <div style={styles.pendingList}>
                {filteredPendingUsers.map(u => (
                  <div key={u.id} style={styles.pendingCard}>
                    <div style={styles.pendingInfo}>
                      <div style={styles.pendingEmail}>{u.email}</div>
                      {u.name && <div style={styles.pendingName}>{u.name}</div>}
                      <div style={styles.pendingMeta}>
                        {u.oauth_provider && (
                          <span style={styles.providerBadge}>{u.oauth_provider}</span>
                        )}
                        {u.requested_tenant && (
                          <span style={styles.tenantBadge}>
                            Requested: {u.requested_tenant.name}
                          </span>
                        )}
                        <span style={styles.dateBadge}>
                          {new Date(u.requested_at || u.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div style={styles.pendingActions}>
                      <button
                        style={styles.approveBtn}
                        onClick={() => {
                          setSelectedUser(u);
                          setModalRole("recruiter");
                          setModalTenant(u.requested_tenant?.id || "");
                          setShowApproveModal(true);
                        }}
                      >
                        ✓ Approve
                      </button>
                      <button
                        style={styles.rejectBtn}
                        onClick={() => handleRejectUser(u)}
                      >
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Users Table */}
        {loading ? (
          <div style={styles.loading}>Loading users...</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Provider</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Created</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={styles.emptyCell}>
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(u => (
                    <tr key={u.id} style={{
                      ...styles.tr,
                      opacity: u.is_disabled ? 0.5 : 1
                    }}>
                      <td style={styles.td}>
                        <div style={styles.userCell}>
                          {u.picture_url ? (
                            <img 
                              src={u.picture_url} 
                              alt="" 
                              style={styles.avatar}
                            />
                          ) : (
                            <div style={styles.avatarPlaceholder}>
                              {u.name?.[0] || u.email?.[0] || "?"}
                            </div>
                          )}
                          <span>{u.name || "—"}</span>
                        </div>
                      </td>
                      <td style={styles.td}>{u.email}</td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.providerBadgeSmall,
                          background: u.oauth_provider === "google" ? "rgba(66, 133, 244, 0.2)" : 
                                     u.oauth_provider === "microsoft" ? "rgba(0, 120, 212, 0.2)" :
                                     "rgba(168, 85, 247, 0.2)",
                          color: u.oauth_provider === "google" ? "#4285f4" : 
                                 u.oauth_provider === "microsoft" ? "#0078d4" :
                                 "#a855f7"
                        }}>
                          {u.oauth_provider || "email"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.statusBadge,
                          background: u.is_disabled ? "rgba(239, 68, 68, 0.2)" : "rgba(34, 197, 94, 0.2)",
                          color: u.is_disabled ? "#ef4444" : "#22c55e"
                        }}>
                          {u.is_disabled ? "Disabled" : "Active"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actions}>
                          <button
                            onClick={() => {
                              setSelectedUser(u);
                              setModalRole("recruiter");
                              setModalTenant("");
                              setShowRoleModal(true);
                            }}
                            style={styles.actionBtn}
                            title="Assign Role"
                          >
                            + Role
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(u);
                              setModalTenant("");
                              setShowTenantModal(true);
                            }}
                            style={styles.actionBtn}
                            title="Add to Tenant"
                          >
                            + Tenant
                          </button>
                          <button
                            onClick={() => handleToggleUserStatus(u)}
                            style={{
                              ...styles.actionBtn,
                              ...(u.is_disabled ? styles.enableBtn : styles.disableBtn)
                            }}
                            title={u.is_disabled ? "Enable User" : "Disable User"}
                          >
                            {u.is_disabled ? "Enable" : "Disable"}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(u);
                              setShowDeleteModal(true);
                            }}
                            style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                            title="Delete User"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Stats */}
        <div style={styles.stats}>
          Total Users: {users.length}
          {selectedTenant && ` (filtered by tenant)`}
          {users.filter(u => u.is_disabled).length > 0 && (
            <span style={{ marginLeft: 16, color: "#ef4444" }}>
              • {users.filter(u => u.is_disabled).length} disabled
            </span>
          )}
        </div>
      </div>

      {/* Assign Role Modal */}
      {showRoleModal && selectedUser && (
        <div style={styles.modalOverlay} onClick={() => setShowRoleModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Assign Role</h3>
            <p style={styles.modalSubtitle}>
              Assigning role to: <strong>{selectedUser.email}</strong>
            </p>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Role</label>
              <select
                value={modalRole}
                onChange={(e) => setModalRole(e.target.value)}
                style={styles.modalSelect}
              >
                {roles.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            
            {modalRole !== "super_admin" && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Tenant (required for admin/recruiter)</label>
                <select
                  value={modalTenant}
                  onChange={(e) => setModalTenant(e.target.value)}
                  style={styles.modalSelect}
                >
                  <option value="">Select tenant...</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowRoleModal(false)}
                style={styles.cancelBtn}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignRole}
                style={styles.saveBtn}
                disabled={saving || (modalRole !== "super_admin" && !modalTenant)}
              >
                {saving ? "Saving..." : "Assign Role"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Tenant Modal */}
      {showTenantModal && selectedUser && (
        <div style={styles.modalOverlay} onClick={() => setShowTenantModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Add User to Tenant</h3>
            <p style={styles.modalSubtitle}>
              Adding: <strong>{selectedUser.email}</strong>
            </p>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Tenant</label>
              <select
                value={modalTenant}
                onChange={(e) => setModalTenant(e.target.value)}
                style={styles.modalSelect}
              >
                <option value="">Select tenant...</option>
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowTenantModal(false)}
                style={styles.cancelBtn}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleAddToTenant}
                style={styles.saveBtn}
                disabled={saving || !modalTenant}
              >
                {saving ? "Adding..." : "Add to Tenant"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve User Modal */}
      {showApproveModal && selectedUser && (
        <div style={styles.modalOverlay} onClick={() => setShowApproveModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Approve User</h3>
            <p style={styles.modalSubtitle}>
              Approving: <strong>{selectedUser.email}</strong>
            </p>
            
            {selectedUser.name && (
              <p style={styles.modalSubtitle}>
                Name: {selectedUser.name}
              </p>
            )}
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Role to Assign</label>
              <select
                value={modalRole}
                onChange={(e) => setModalRole(e.target.value)}
                style={styles.modalSelect}
              >
                {roles.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            
            {modalRole !== "super_admin" && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Assign to Tenant</label>
                <select
                  value={modalTenant}
                  onChange={(e) => setModalTenant(e.target.value)}
                  style={styles.modalSelect}
                >
                  <option value="">Select tenant...</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            <div style={styles.approveNote}>
              Once approved, the user will receive an email notification and can sign in to access the platform.
            </div>
            
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowApproveModal(false)}
                style={styles.cancelBtn}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleApproveUser}
                style={styles.approveSaveBtn}
                disabled={saving || (modalRole !== "super_admin" && !modalTenant)}
              >
                {saving ? "Approving..." : "✓ Approve User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div style={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ ...styles.modalTitle, color: "#ef4444" }}>Delete User</h3>
            <p style={styles.modalSubtitle}>
              Are you sure you want to delete <strong>{selectedUser.email}</strong>?
            </p>
            
            <div style={styles.deleteWarning}>
              ⚠️ This action cannot be undone. The user will lose all access and their account will be permanently removed.
            </div>
            
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={styles.cancelBtn}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                style={styles.deleteSaveBtn}
                disabled={saving}
              >
                {saving ? "Deleting..." : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)",
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "1px solid rgba(255,255,255,0.1)"
  },
  logoContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  logoImage: {
    height: 48,
    width: "auto",
    objectFit: "contain"
  },
  subtitle: {
    fontSize: 14,
    color: "#9ca3af"
  },
  backButton: {
    padding: "8px 16px",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer"
  },
  content: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "40px 20px"
  },
  pageTitle: {
    fontSize: 28,
    marginBottom: 8
  },
  pageSubtitle: {
    color: "#9ca3af",
    marginBottom: 32,
    fontSize: 16
  },
  filters: {
    display: "flex",
    gap: 12,
    marginBottom: 24,
    flexWrap: "wrap"
  },
  searchInput: {
    flex: 1,
    minWidth: 200,
    padding: "12px 16px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color: "#fff",
    fontSize: 14
  },
  select: {
    padding: "12px 16px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color: "#fff",
    fontSize: 14,
    minWidth: 150
  },
  refreshBtn: {
    padding: "12px 16px",
    background: "rgba(255,255,255,0.1)",
    border: "none",
    borderRadius: 8,
    color: "#fff",
    cursor: "pointer",
    fontSize: 14
  },
  error: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 24,
    color: "#ef4444"
  },
  errorDismiss: {
    background: "none",
    border: "none",
    color: "#ef4444",
    cursor: "pointer",
    fontSize: 16
  },
  loading: {
    textAlign: "center",
    padding: 48,
    color: "#9ca3af"
  },
  tableContainer: {
    background: "rgba(255,255,255,0.02)",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.1)"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse"
  },
  th: {
    textAlign: "left",
    padding: "14px 16px",
    background: "rgba(255,255,255,0.03)",
    color: "#9ca3af",
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    borderBottom: "1px solid rgba(255,255,255,0.1)"
  },
  tr: {
    borderBottom: "1px solid rgba(255,255,255,0.05)"
  },
  td: {
    padding: "14px 16px",
    fontSize: 14
  },
  emptyCell: {
    padding: 48,
    textAlign: "center",
    color: "#6b7280"
  },
  userCell: {
    display: "flex",
    alignItems: "center",
    gap: 12
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    objectFit: "cover"
  },
  avatarPlaceholder: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "uppercase"
  },
  providerBadgeSmall: {
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 500,
    textTransform: "capitalize"
  },
  statusBadge: {
    padding: "4px 10px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500
  },
  actions: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap"
  },
  actionBtn: {
    padding: "6px 10px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 12,
    cursor: "pointer"
  },
  disableBtn: {
    background: "rgba(251, 191, 36, 0.1)",
    borderColor: "rgba(251, 191, 36, 0.3)",
    color: "#fbbf24"
  },
  enableBtn: {
    background: "rgba(34, 197, 94, 0.1)",
    borderColor: "rgba(34, 197, 94, 0.3)",
    color: "#22c55e"
  },
  deleteBtn: {
    background: "rgba(239, 68, 68, 0.1)",
    borderColor: "rgba(239, 68, 68, 0.3)",
    color: "#ef4444"
  },
  stats: {
    marginTop: 16,
    padding: "12px 16px",
    background: "rgba(255,255,255,0.02)",
    borderRadius: 8,
    color: "#9ca3af",
    fontSize: 13
  },
  // Pending users section
  pendingSection: {
    background: "rgba(251, 191, 36, 0.1)",
    border: "1px solid rgba(251, 191, 36, 0.3)",
    borderRadius: 12,
    marginBottom: 24,
    overflow: "hidden"
  },
  pendingHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    cursor: "pointer",
    background: "rgba(251, 191, 36, 0.05)"
  },
  pendingTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: "#fbbf24"
  },
  toggleIcon: {
    color: "#fbbf24",
    fontSize: 12
  },
  pendingList: {
    padding: "12px 20px 20px"
  },
  pendingCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 16px",
    background: "rgba(255,255,255,0.03)",
    borderRadius: 8,
    marginBottom: 10,
    border: "1px solid rgba(255,255,255,0.1)"
  },
  pendingInfo: {
    flex: 1
  },
  pendingEmail: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 4
  },
  pendingName: {
    fontSize: 13,
    color: "#9ca3af",
    marginBottom: 6
  },
  pendingMeta: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap"
  },
  providerBadge: {
    fontSize: 11,
    padding: "2px 8px",
    background: "rgba(96, 165, 250, 0.2)",
    color: "#60a5fa",
    borderRadius: 4,
    textTransform: "capitalize"
  },
  tenantBadge: {
    fontSize: 11,
    padding: "2px 8px",
    background: "rgba(251, 191, 36, 0.2)",
    color: "#fbbf24",
    borderRadius: 4
  },
  dateBadge: {
    fontSize: 11,
    padding: "2px 8px",
    background: "rgba(255,255,255,0.1)",
    color: "#9ca3af",
    borderRadius: 4
  },
  pendingActions: {
    display: "flex",
    gap: 8,
    marginLeft: 16
  },
  approveBtn: {
    padding: "8px 16px",
    background: "#22c55e",
    border: "none",
    borderRadius: 6,
    color: "#fff",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer"
  },
  rejectBtn: {
    padding: "8px 16px",
    background: "rgba(239, 68, 68, 0.2)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: 6,
    color: "#ef4444",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer"
  },
  // Modals
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  },
  modal: {
    background: "#1a2632",
    borderRadius: 12,
    padding: 24,
    width: "100%",
    maxWidth: 400,
    border: "1px solid rgba(255,255,255,0.1)"
  },
  modalTitle: {
    margin: "0 0 8px 0",
    fontSize: 18,
    fontWeight: 600
  },
  modalSubtitle: {
    margin: "0 0 20px 0",
    color: "#9ca3af",
    fontSize: 14
  },
  formGroup: {
    marginBottom: 16
  },
  label: {
    display: "block",
    marginBottom: 6,
    color: "#9ca3af",
    fontSize: 13
  },
  modalSelect: {
    width: "100%",
    padding: "10px 12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color: "#fff",
    fontSize: 14
  },
  modalActions: {
    display: "flex",
    gap: 12,
    marginTop: 24
  },
  cancelBtn: {
    flex: 1,
    padding: 10,
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color: "#9ca3af",
    cursor: "pointer",
    fontSize: 14
  },
  saveBtn: {
    flex: 1,
    padding: 10,
    background: "#3b82f6",
    border: "none",
    borderRadius: 8,
    color: "#fff",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500
  },
  approveNote: {
    padding: 12,
    background: "rgba(34, 197, 94, 0.1)",
    border: "1px solid rgba(34, 197, 94, 0.2)",
    borderRadius: 8,
    fontSize: 13,
    color: "#86efac",
    marginTop: 16
  },
  approveSaveBtn: {
    flex: 1,
    padding: 10,
    background: "#22c55e",
    border: "none",
    borderRadius: 8,
    color: "#fff",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500
  },
  deleteWarning: {
    padding: 12,
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.2)",
    borderRadius: 8,
    fontSize: 13,
    color: "#fca5a5",
    marginTop: 8
  },
  deleteSaveBtn: {
    flex: 1,
    padding: 10,
    background: "#ef4444",
    border: "none",
    borderRadius: 8,
    color: "#fff",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500
  }
};
